import { useCallback } from "react"
import { useMutation } from "urql"

import { GL_ACCOUNT_ERROR_MESSAGES } from "../constants/errorMessages"

import { UpdateGlAccountDocument } from "@/graphql/codegen/graphql"
import type { GlAccountUpsertInput } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { ActionOutcome } from "@/types/common"

interface UseUpdateGlAccountResult {
  updateGlAccount: (input: GlAccountUpsertInput) => Promise<ActionOutcome>
  isUpdating: boolean
}

export const useUpdateGlAccount = (): UseUpdateGlAccountResult => {
  const [{ fetching }, executeMutation] = useMutation(UpdateGlAccountDocument)
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const updateGlAccount = useCallback(
    async (input: GlAccountUpsertInput): Promise<ActionOutcome> => {
      const result = await executeMutation({ input })

      if (result.error) {
        const errorMessage = result.error.message || "Failed to update GL account"

        if (errorMessage.includes(GL_ACCOUNT_ERROR_MESSAGES.ALREADY_EXISTS)) {
          return { success: false, error: new Error(errorMessage) }
        }

        notify({
          type: NotificationType.Error,
          silent: false,
          message: errorMessage,
        })
        return { success: false }
      }

      if (result.data?.glAccount.update.__typename === "GlAccountFailure") {
        const errorMessage = result.data.glAccount.update.error.message || "Failed to update GL account"

        if (errorMessage.includes(GL_ACCOUNT_ERROR_MESSAGES.ALREADY_EXISTS)) {
          return { success: false, error: new Error(errorMessage) }
        }

        notify({
          type: NotificationType.Error,
          silent: false,
          message: errorMessage,
        })
        return { success: false }
      }

      notify({
        type: NotificationType.Success,
        silent: false,
        message: "Changes to the account have been saved",
      })
      return { success: true }
    },
    [executeMutation, notify]
  )

  return {
    updateGlAccount,
    isUpdating: fetching,
  }
}
